import { NetworkStatus, gql, useMutation } from '@apollo/client';
import { useUserContext } from '@do/seashell/hooks';
import { Organization } from '@do/seashell/types/Shell';
import Cookies from 'js-cookie';
import React, { useEffect } from 'react';

export const MARKETPLACE_ORIGIN = 'https://marketplace.digitalocean.com';
export const PDOCS_ORIGIN = 'https://docs.digitalocean.com';
export const ALLOWED_ORIGINS = [MARKETPLACE_ORIGIN, PDOCS_ORIGIN];
export const REMEMBER_ME_COOKIE_NAME = '_digitalocean_remember_me';

export const SwitchContextMutation = gql`
  mutation switchContext($contextId: Int!, $context_urn: String) {
    switchContext(context_id: $contextId, context_urn: $context_urn) {
      status
    }
  }
`;

const AppIndex: React.FC = () => {
  const userContext = useUserContext();
  const [switchContext] = useMutation(SwitchContextMutation);

  const user = userContext?.user;
  const userRefetch = userContext?.refetch;
  const isUnauthorized = userContext?.networkStatus === NetworkStatus.error;
  const hasRememberMeCookie = !!Cookies.get(REMEMBER_ME_COOKIE_NAME);

  useEffect(() => {
    const referrer = document.referrer;
    if (ALLOWED_ORIGINS.includes(referrer)) {
      window?.top?.postMessage(
        { user, isUnauthorized, hasRememberMeCookie },
        referrer
      );
    }
  }, [user, user.id, isUnauthorized, hasRememberMeCookie]);

  useEffect(() => {
    const messageEventListener = async (event: MessageEvent): Promise<void> => {
      if (!ALLOWED_ORIGINS.includes(event.origin)) {
        return;
      }

      const switchContextId = parseInt(event.data.switchContextId, 10);
      const isContextIdValid =
        typeof switchContextId === 'number' && switchContextId > 0;

      if (isContextIdValid) {
        const isUserContext = parseInt(user.id, 10) === switchContextId;

        const nextContext = user.organizations.find(
          (org: Organization) => org.id === switchContextId
        );

        await switchContext({
          variables: {
            contextId: switchContextId,
            context_urn: isUserContext
              ? `do:user:${user.uuid}`
              : `do:team:${nextContext?.uuid}`
          }
        });

        await userRefetch();
      }
    };

    window.addEventListener('message', messageEventListener);

    return () => {
      window.removeEventListener('message', messageEventListener);
    };
  });

  return null;
};

export default AppIndex;
